import CompactInventoryCard from "@Components/CompactInventoryCard";
import { useEffect, useState } from "react";
import classes from "./SimilarProperties.module.scss";
import SectionContainer from "@Pages/PropertyPage/components/SectionContainer";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { getInventorySimilarById } from "./helper";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import HorizontalScroll from "@Components/HorizontalScroll";
import LoaderIcon from "@Icons/LoaderIcon";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import useIsDesktop from "hooks/useIsDesktop";
import isAvailable from "@Utils/isAvailable";
import { APP_PAGE } from "@Utils/webApputils/analyticEvents";

const SSR_LIMIT = 5;

const SimilarProperties = ({ isTopSection = false, isNearbyProperties = false }) => {
  const { inventory, nearbyProperties } = useSelector(
    ({ propertyPageData }: AppState) => ({
      inventory: propertyPageData.data,
      nearbyProperties: propertyPageData.nearby_properties,
    }),
    shallowEqual,
  );
  const { t } = useTranslationHook();
  const [listings, setListings] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(!isNearbyProperties);
  const isDesktop = useIsDesktop();
  const isPropertyAvailable = isAvailable(inventory);
  const isSoldOut = !isPropertyAvailable;
  const { id, userCurrency } = useSelector(
    ({ propertyPageData, appData }: AppState) => ({
      id: propertyPageData?.data?.id,
      userCurrency: appData.userCurrency,
    }),
    shallowEqual,
  );

  // For nearby properties, show first 5 items during SSR, full list on client
  const [nearbyDisplayList, setNearbyDisplayList] = useState<any>(
    isNearbyProperties && nearbyProperties
      ? nearbyProperties.slice(0, SSR_LIMIT)
      : nearbyProperties,
  );

  useEffect(() => {
    if (isNearbyProperties && nearbyProperties && nearbyProperties.length > SSR_LIMIT) {
      setNearbyDisplayList(nearbyProperties);
    }
  }, [nearbyProperties]);

  const getListings = async () => {
    const options = {
      inventory_id: id,
      methods: "meta_short,amber_sales",
      user_currency: userCurrency,
    };
    try {
      const res = await getInventorySimilarById(options);
      if (res) {
        if (res.length > 0) {
          setListings(res);
        } else {
          const event = new Event("navigation_section_removed");
          window.dispatchEvent(event);
        }
        setIsLoading(false);
      } else {
        setListings([]);
        setIsLoading(false);
      }
    } catch {
      setListings([]);
      setIsLoading(false);
    }
  };

  const getHeadlineBasedOnIsNearbyProperties = () => {
    if (isNearbyProperties) {
      return isTopSection
        ? t("propertypage.similarproperties.nearbyavailable")
        : t("propertypage.similarproperties.nearby");
    }
    return isTopSection
      ? t("propertypage.similarproperties.similaravailable")
      : t("propertypage.similarproperties.similar");
  };

  useEffect(() => {
    if (
      (isTopSection && isSoldOut && !isNearbyProperties) ||
      (!isTopSection && !isSoldOut && !isNearbyProperties)
    )
      getListings();
    else setIsLoading(false);
  }, []);

  if (!listings.length && isLoading) {
    return (
      <section
        style={{ height: "340px", display: "flex", alignItems: "center", justifyContent: "center" }}
        id={isNearbyProperties ? "nearby_properties" : "similar_properties"}
      >
        <LoaderIcon />
      </section>
    );
  }
  return (
    <>
      {(isNearbyProperties ? nearbyDisplayList : listings)?.length > 0 && (
        <SectionContainer
          title={getHeadlineBasedOnIsNearbyProperties()}
          id={isNearbyProperties ? "nearby_properties" : "similar_properties"}
          className={formatClassNames(
            classes.container,
            isDesktop ? classes.desktop : "",
            !isTopSection && isDesktop ? classes.bottomMargin : "",
            isNearbyProperties && classes.largeScreenResponsive,
          )}
          isDesktop={isDesktop}
          showTitleLine
          headingLevel={2}
        >
          <HorizontalScroll
            className={formatClassNames(
              classes.scrollContainer,
              isDesktop ? classes.scrollContainerDesktop : "",
            )}
            showControls={isDesktop}
          >
            {(isNearbyProperties ? nearbyDisplayList : listings).map((item: any) => (
              <CompactInventoryCard
                item={item}
                key={item.id}
                isRequestActive={isLoading}
                isDesktop={isDesktop}
                showIcons
                gtagEventAction="clicked_similar_properties"
                screenName={APP_PAGE.PROPERTY}
              />
            ))}
          </HorizontalScroll>
        </SectionContainer>
      )}
    </>
  );
};

export default wrapperHOC(SimilarProperties, {
  componentName: "SimilarProperties-SearchPage",
  showForChina: true,
  showForWhiteLable: false,
});
